import { useRef, type KeyboardEvent, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface TabItem<T extends string = string> {
  id: T
  label: string
  /** One line explaining what this tab does, shown under the strip. */
  hint?: string
  icon?: ReactNode
  count?: number
}

export interface TabsProps<T extends string = string> {
  items: TabItem<T>[]
  value: T
  onChange: (id: T) => void
  /** Names the strip for assistive tech, e.g. "Ingestion method". */
  label: string
  /** Prefix for the generated tab/panel ids, so `TabPanel` can match them. */
  idPrefix: string
  className?: string
}

/**
 * A tab strip that behaves like one.
 *
 * The previous hand-rolled version announced `role="tablist"` without ever
 * rendering a `tabpanel`, without `aria-controls`, and with every tab in the
 * tab order — so a screen reader was told "tab 1 of 3" and then handed nothing
 * to move to. Here the strip is a single tab stop and Left/Right/Home/End move
 * between tabs, which is what the role promises.
 */
export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
  label,
  idPrefix,
  className,
}: TabsProps<T>) {
  const stripRef = useRef<HTMLDivElement>(null)

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = items.findIndex((item) => item.id === value)
    let next = index
    if (event.key === 'ArrowRight') next = (index + 1) % items.length
    else if (event.key === 'ArrowLeft') next = index <= 0 ? items.length - 1 : index - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = items.length - 1
    else return

    event.preventDefault()
    onChange(items[next].id)
    stripRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      [next]?.focus()
  }

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        'scrollbar-slim -mb-px flex gap-1 overflow-x-auto border-b border-line',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.id === value
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.id}`}
            aria-selected={active}
            aria-controls={`${idPrefix}-panel-${item.id}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5',
              'text-base font-medium transition-colors duration-100 ease-out',
              active
                ? 'border-accent text-ink'
                : 'border-transparent text-ink-muted hover:border-line-strong hover:text-ink',
            )}
          >
            {item.icon}
            {item.label}
            {item.count !== undefined ? (
              <span
                className={cn(
                  'tabular rounded-full px-1.5 text-2xs',
                  active ? 'bg-accent-soft text-accent-text' : 'bg-surface-sunken text-ink-muted',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export function TabPanel({
  id,
  idPrefix,
  active,
  children,
  className,
}: {
  id: string
  idPrefix: string
  active: boolean
  children: ReactNode
  className?: string
}) {
  if (!active) return null
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
    >
      {children}
    </div>
  )
}
